import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";

/**
 * Presentation of the measurements table's columns — everything the API leaves
 * to the frontend (names, units, order, decimals), keyed by the column `key`
 * the API sends. The API decides which columns exist; this decides how they
 * look. Order here is the rendered order. Names follow Daily Monitoring's
 * wording for the same measurements.
 */
export const MEASUREMENT_COLUMNS: MeasurementColumnSpec[] = [
  { key: "dri", category: "ppg", label: msg`DRI`, decimals: 1 },
  {
    key: "lvfp",
    category: "ppg",
    label: msg`dLVFP`,
    unit: "mmHg",
    decimals: 1,
  },
  {
    key: "lvfp_median",
    category: "ppg",
    label: msg`LVFP median`,
    unit: "mmHg",
    decimals: 1,
  },
  {
    key: "af_probability",
    category: "ppg",
    label: msg`AF probability`,
    unit: "%",
    decimals: 0,
    // The API sends a 0–1 probability; the table shows an integer percentage.
    scale: 100,
  },
  { key: "hr", category: "ppg", label: msg`HR`, unit: "bpm", decimals: 0 },
  { key: "spo2", category: "ppg", label: msg`SpO₂`, unit: "%", decimals: 0 },
  { key: "quality", category: "ppg", label: msg`Quality` },
  { key: "bp", category: "vitals", label: msg`BP`, unit: "mmHg", decimals: 0 },
  {
    key: "weight",
    category: "vitals",
    label: msg`Weight`,
    unit: "kg",
    decimals: 1,
  },
  {
    key: "symptom_shortness_of_breath",
    category: "symptoms",
    label: msg`Breathless`,
    decimals: 0,
  },
  {
    key: "symptom_fatigue_score",
    category: "symptoms",
    label: msg`Fatigue`,
    decimals: 0,
  },
  {
    key: "symptom_perceived_health_change",
    category: "symptoms",
    label: msg`Health Change`,
    decimals: 0,
  },
  { key: "ecg_rhythm", category: "ecg", label: msg`Rhythm` },
  { key: "quicklook_ppg", category: "actions", label: msg`QuickLook` },
  { key: "open_ecg", category: "actions", label: msg`ECG` },
];

/**
 * The group header each category spans, worded as Daily Monitoring's groups.
 * Adjacent columns sharing a group name span one cell, so each must resolve to
 * one exact string.
 */
export const MEASUREMENT_CATEGORIES: Record<
  MeasurementCategory,
  MessageDescriptor
> = {
  ppg: msg`PPG derived`,
  vitals: msg`Vitals`,
  symptoms: msg`Symptoms`,
  ecg: msg`ECG`,
  actions: msg`Actions`,
};

/**
 * Where a column sits: its group header, and beyond that `ppg` columns are the
 * detail dialog's click band, `actions` are pinned right, and unknown symptoms
 * slot in after the known ones.
 */
export type MeasurementCategory =
  | "ppg"
  | "vitals"
  | "symptoms"
  | "ecg"
  | "actions";

export interface MeasurementColumnSpec {
  key: string;
  category: MeasurementCategory;
  /**
   * The measurement's name, without its unit. A plain string for a name that
   * comes from data, shown as-is.
   */
  label: MessageDescriptor | string;
  /** A unit symbol — the same in every language, so not translated. */
  unit?: string;
  decimals?: number;
  scale?: number;
}

const SYMPTOM_PREFIX = "symptom_";

/**
 * The spec for a column the API sent, in rendered order. A symptom the
 * registry doesn't know yet (the API adds one column per configured or present
 * symptom) still renders, labelled from its key, after the known ones.
 * Anything else unknown is dropped: its cell shape can't be assumed.
 */
export function orderedColumnSpecs(keys: string[]): MeasurementColumnSpec[] {
  const known = MEASUREMENT_COLUMNS.filter((spec) => keys.includes(spec.key));
  const extraSymptoms = keys
    .filter(
      (key) =>
        key.startsWith(SYMPTOM_PREFIX) &&
        !MEASUREMENT_COLUMNS.some((spec) => spec.key === key),
    )
    .map(
      (key): MeasurementColumnSpec => ({
        key,
        category: "symptoms",
        label: key.slice(SYMPTOM_PREFIX.length).replace(/_/g, " "),
        decimals: 0,
      }),
    );
  const afterSymptoms =
    known.map((spec) => spec.category).lastIndexOf("symptoms") + 1;
  const insertAt =
    afterSymptoms > 0
      ? afterSymptoms
      : known.findIndex(
          (spec) => spec.category === "ecg" || spec.category === "actions",
        );
  return insertAt < 0
    ? [...known, ...extraSymptoms]
    : [...known.slice(0, insertAt), ...extraSymptoms, ...known.slice(insertAt)];
}
